import { useEffect, useState, useCallback } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { Gauge, Zap, RefreshCw, StopCircle } from "lucide-react";

export default function StationsLive() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await apiClient.get("/admin/stations/live");
      setRows(r.data || []);
    } catch (e) { toast.error(getErrorMessage(e, "Load failed")); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const endSession = async (sid) => {
    if (!window.confirm("End this active session now?")) return;
    try { await apiClient.post(`/admin/sessions/${sid}/end`); toast.success("Session ended"); load(); }
    catch (e) { toast.error(getErrorMessage(e, "End failed")); }
  };

  const fmtTime = (t) => t ? new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <AdminLayout>
      <div className="p-4 md:p-8 space-y-6 max-w-6xl">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3"><Gauge className="w-8 h-8 text-pink-400" /> Live station grid</h1>
            <p className="text-slate-400 mt-1">Real-time view of active sim sessions — auto-refreshes every 8s.</p>
          </div>
          <Button onClick={load} className="bg-white/10 hover:bg-white/15" data-testid="stations-live-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="text-slate-400 text-center p-12">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {rows.map((s) => {
              const active = s.state === "active";
              const stale = s.stale;
              const bg = stale ? "from-amber-500/20 to-red-500/10" : active ? "from-emerald-500/20 to-emerald-500/5" : "from-slate-800/40 to-slate-800/10";
              const border = stale ? "border-amber-500/40" : active ? "border-emerald-500/40" : "border-white/10";
              return (
                <Card key={s.station_id} data-testid={`station-live-${s.station_id}`}
                  className={`p-5 border ${border} bg-gradient-to-br ${bg}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-white font-bold text-lg">{s.station_name}</div>
                    <div className={`text-xs font-bold px-2 py-1 rounded ${active ? "bg-emerald-500/30 text-emerald-100" : "bg-white/10 text-slate-400"}`}>
                      {stale ? "STALE" : active ? "ACTIVE" : "FREE"}
                    </div>
                  </div>
                  <div className="text-xs text-slate-400 mb-3 uppercase">{s.type?.replace(/_/g, " ")}</div>

                  {active && s.session ? (
                    <>
                      <div className="text-slate-200 font-semibold">{s.session.user_name}</div>
                      <div className="text-slate-500 text-xs">{s.session.user_email}</div>
                      <div className="text-slate-400 text-sm mt-2 flex items-center gap-1">
                        <Zap className="w-4 h-4 text-emerald-400" /> Started {fmtTime(s.session.started_at)}
                      </div>
                      {s.session.best_lap_ms && (
                        <div className="text-emerald-300 text-xs mt-1">Best lap: {(s.session.best_lap_ms / 1000).toFixed(3)}s</div>
                      )}
                      <Button onClick={() => endSession(s.session.id)}
                        className="w-full mt-3 bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/40"
                        data-testid={`station-end-${s.station_id}`}>
                        <StopCircle className="w-4 h-4 mr-2" /> End session
                      </Button>
                    </>
                  ) : (
                    <div className="text-slate-500 text-sm">No active session</div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
